import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { sampleTracking } from "@/mocks/courier";

type Status = "idle" | "empty" | "invalid" | "success";

export default function CourierTrack() {
  const [code, setCode] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [searchedCode, setSearchedCode] = useState("");

  usePageMeta(
    "快递查询 - 美味的快递 | 实时查询物流轨迹",
    "输入运单号即可在美味的快递查询包裹的完整物流轨迹，从揽收、运输到签收，实时掌握配送进度，方便又省心。",
    "美味的快递,快递查询,物流查询,运单查询,快递追踪,同城配送"
  );

  const query = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setStatus("empty");
      return;
    }
    if (!/^[A-Za-z0-9]{8,}$/.test(trimmed)) {
      setStatus("invalid");
      return;
    }
    setSearchedCode(trimmed.toUpperCase());
    setStatus("success");
  };

  const doneCount = sampleTracking.filter((s) => s.done).length;
  const progress = Math.round((doneCount / sampleTracking.length) * 100);

  return (
    <div className="bg-background-50">
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "快递服务", to: "/courier" }, { label: "快递查询" }]} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">快递查询</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            输入运单号，即可查看从揽收、运输到签收的完整物流轨迹，随时掌握包裹动态。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <label className="mb-2 block font-heading text-base font-extrabold text-foreground-950">运单号 / 手机号</label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition focus-within:border-primary-400">
                  <i className="ri-barcode-line text-foreground-400 text-lg" />
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") query(code);
                    }}
                    placeholder="请输入运单号，如 MW20261012001"
                    className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => query(code)}
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-8 py-3.5 text-sm font-bold text-background-50 transition hover:bg-primary-600"
                >
                  <i className="ri-search-eye-line" />
                  立即查询
                </button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                <span>演示单号：</span>
                <button
                  type="button"
                  onClick={() => {
                    setCode("MW20261012001");
                    query("MW20261012001");
                  }}
                  className="rounded-full bg-background-100 px-3 py-1 font-medium text-primary-600 transition hover:bg-primary-50"
                >
                  MW20261012001
                </button>
              </div>
            </div>

            {status === "empty" && (
              <div className="mt-6 flex items-center gap-3 rounded-lg border border-background-200 bg-primary-50 px-5 py-4 text-sm text-foreground-700">
                <i className="ri-alert-line text-lg text-primary-600" />
                请输入运单号后再查询哦
              </div>
            )}
            {status === "invalid" && (
              <div className="mt-6 flex items-center gap-3 rounded-lg border border-background-200 bg-primary-50 px-5 py-4 text-sm text-foreground-700">
                <i className="ri-error-warning-line text-lg text-primary-600" />
                运单号格式不正确，请检查后重新输入（应为 8 位以上的字母或数字）
              </div>
            )}

            {status === "success" && (
              <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-100 pb-5">
                  <div>
                    <p className="text-xs text-foreground-500">运单号</p>
                    <p className="mt-1 font-heading text-lg font-extrabold tracking-wide text-foreground-950">{searchedCode}</p>
                  </div>
                  <span className="flex items-center gap-1.5 rounded-full bg-accent-100 px-4 py-1.5 text-sm font-bold text-accent-700">
                    <i className="ri-checkbox-circle-fill" />
                    已签收
                  </span>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>配送进度</span>
                    <span className="font-bold text-primary-600">{progress}%</span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                    <div className="h-full rounded-full bg-accent-500 transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>

                <ol className="mt-7 flex flex-col">
                  {sampleTracking.map((step, i) => (
                    <li key={step.title} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded-full ${
                            step.done ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-500"
                          }`}
                        >
                          <i className={i === 0 ? "ri-star-fill text-sm" : "ri-check-line text-sm"} />
                        </span>
                        {i < sampleTracking.length - 1 && <span className="my-1 w-px flex-1 bg-background-200" />}
                      </div>
                      <div className="flex-1 pb-6">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className={`font-heading text-sm font-bold ${i === 0 ? "text-accent-700" : "text-foreground-900"}`}>
                            {step.title}
                          </p>
                          <span className="text-xs text-foreground-400">{step.time}</span>
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-foreground-500">{step.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <p className="rounded-lg bg-background-100 px-4 py-3 text-xs text-foreground-500">
                  * 以上轨迹为前端演示数据，实际物流信息以真实运单为准。
                </p>
              </div>
            )}

            {status === "idle" && (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-box-3-line text-3xl" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">暂无查询结果</h3>
                <p className="mt-2 text-sm text-foreground-500">输入运单号并点击「立即查询」，即可查看物流轨迹</p>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
                <i className="ri-question-line text-primary-600" />
                查询小贴士
              </h2>
              <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  运单号可在下单成功的短信或 App 订单详情中查看。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  刚下单的订单可能还未生成轨迹，请 5 分钟后再试。
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-accent-600" />
                  也可用收件人手机号查询名下所有在途订单。
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-base font-extrabold text-foreground-950">还没有订单？</h2>
              <p className="mt-2 text-sm text-foreground-500">现在就下单寄件，最快 30 分钟上门取件。</p>
              <Link
                to="/courier-send"
                className="mt-5 flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-bold text-background-50 transition hover:bg-accent-600"
              >
                <i className="ri-send-plane-fill" />
                立即寄件
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}